import { ProgressBar } from 'primereact/progressbar';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import { Button } from 'primereact/button';
import {
  LoadedRecomendacaoSemeaduraStatus,
  RecomendacaoSemeaduraSimple,
} from '../../@types';
import { RecomendacaoSemeaduraService } from '../../services';
import Agro1Toast from '../../components/Agro1Toast';
import SituacaoFieldTooltip from './situacao-field-tooltip';
import { situacaoParse } from '../../utils/constants';

const SituacaoField = ({
  item,
  loadedSituacoesSetter,
  loadedSituacoes,
}: SituacaoFieldProps) => {
  const situacaoParsed = situacaoParse[item.situacao];
  const [isLoadingStatus, setLoadingStatus] = useState(false);

  const thisStatus = useMemo(
    () => loadedSituacoes.find((existingItem) => existingItem.id === item.id),
    [loadedSituacoes, item],
  );

  const alreadyLoaded = useMemo(() => !!thisStatus, [thisStatus]);

  const formatedPercentual = useMemo(
    () =>
      thisStatus
        ? formatNumber(thisStatus.percentual * 100, {
            maximumFractionDigits: 0,
          })
        : '',
    [thisStatus],
  );

  const formatedAreas = useMemo(() => {
    if (!thisStatus) return '';

    const aplicada = formatNumber(thisStatus.aplicada, {
      maximumFractionDigits: 1,
    });
    const recomendada = formatNumber(thisStatus.recomendada, {
      maximumFractionDigits: 1,
    });

    return `${aplicada}ha de ${recomendada}ha`;
  }, [thisStatus]);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      if (alreadyLoaded) return;

      const newStatus = await RecomendacaoSemeaduraService.status({
        id: item.id,
        propriedadeId: item.propriedade.id,
      });

      loadedSituacoesSetter((old) => [...old, { id: item.id, ...newStatus }]);
    } catch (error) {
      console.error(error);

      Agro1Toast.show({
        detail: 'Erro ao carregar o status da recomendação.',
        severity: 'error',
        sticky: true,
      });
    } finally {
      setLoadingStatus(false);
    }
  }, [item, alreadyLoaded, loadedSituacoesSetter]);

  const label = useMemo(
    () => (
      <div className="flex gap-1">
        {item.situacao === 'em_andamento' && (
          <Button
            loading={isLoadingStatus}
            loadingIcon={<i className="pi pi-spin pi-spinner" color="white" />}
            icon="pi pi-info-circle"
            rounded
            severity="secondary"
            className="button-info"
            size="small"
            onClick={loadStatus}
          />
        )}
        <div className={`situacao ${situacaoParsed.className}`}>
          {situacaoParsed.label}
        </div>
      </div>
    ),
    [
      item.situacao,
      situacaoParsed.className,
      loadStatus,
      isLoadingStatus,
      situacaoParsed.label,
    ],
  );

  const progress = useMemo(
    () => (
      <div className="flex gap-1 align-items-center">
        <SituacaoFieldTooltip glebas={thisStatus?.glebas || []} />
        <div className="flex flex-column w-full">
          <ProgressBar
            value={formatedPercentual}
            aria-label="Percentual"
            color="#27ae61"
            className="situacao p-0"
          />
          <span className="situacao">{formatedAreas}</span>
        </div>
      </div>
    ),
    [formatedAreas, formatedPercentual, thisStatus],
  );

  return (
    <div className="recomendacoes-semeadura-page">
      {thisStatus ? progress : label}
    </div>
  );
};

type SituacaoFieldProps = {
  item: RecomendacaoSemeaduraSimple;
  loadedSituacoesSetter: Dispatch<
    SetStateAction<LoadedRecomendacaoSemeaduraStatus[]>
  >;
  loadedSituacoes: LoadedRecomendacaoSemeaduraStatus[];
};

export default SituacaoField;
